import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, X, Radio } from "lucide-react";
import { CATEGORIES } from "../lib/categories";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import InstallApp from "./InstallApp";

const CLOCK_OPTS = { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" };

const Ticker = ({ items }) => {
  if (!items.length) return null;
  const doubled = [...items, ...items];
  return (
    <div className="bg-[#0F172A] text-white overflow-hidden flex items-stretch" data-testid="breaking-ticker">
      <div className="bg-[#E11D48] px-3 sm:px-4 flex items-center gap-2 shrink-0 z-10">
        <Radio size={14} className="live-dot" />
        <span className="meta-label font-bold hidden sm:inline">En direct</span>
      </div>
      <div className="overflow-hidden flex items-center flex-1">
        <div className="ticker-track">
          {doubled.map((a, i) => (
            <Link key={i} to={`/${a.category}/${a.slug}`} className="px-6 py-2 font-sub text-sm hover:text-[#FDA4AF] whitespace-nowrap border-r border-white/10">
              <span className="text-[#FDA4AF] mr-2">›</span>{a.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Header() {
  const [now, setNow] = useState(new Date());
  const [ticker, setTicker] = useState([]);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [weather, setWeather] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    api.get("/articles", { params: { limit: 8 } }).then((r) => setTicker(r.data.articles || [])).catch(() => {});
    fetch("https://api.open-meteo.com/v1/forecast?latitude=5.36&longitude=-4.03&current=temperature_2m")
      .then((r) => r.json())
      .then((d) => { if (d?.current?.temperature_2m != null) setWeather(Math.round(d.current.temperature_2m)); })
      .catch(() => {});
    return () => clearInterval(t);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/recherche?q=${encodeURIComponent(search.trim())}`);
    setSearchOpen(false);
  };

  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

  return (
    <header className="bg-[#FAFAF8]">
      {/* Masthead */}
      <div className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
          <span className="meta-label text-slate-500 capitalize hidden sm:block" data-testid="masthead-date">{dateStr}</span>
          <div className="flex items-center gap-3 sm:gap-4">
            <InstallApp />
            <span className="font-mono-u text-xs text-slate-500 tabular-nums" data-testid="masthead-clock">{now.toLocaleTimeString("fr-FR", CLOCK_OPTS)} GMT</span>
            <span className="meta-label text-slate-400 hidden md:inline">Abidjan {weather != null ? `${weather}°C` : "…"}</span>
          </div>
        </div>
      </div>

      {/* Brand */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center">
        <Link to="/" data-testid="brand-logo" className="inline-block">
          <img src="/logo-web.png" alt="Le Véridique — L'information en toute vérité" className="h-20 sm:h-24 lg:h-28 w-auto mx-auto" />
        </Link>
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#0F172A] text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="hidden lg:flex items-center">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={c.featuredPage ? "/a-la-une" : `/rubrique/${c.slug}`}
                data-testid={`nav-link-${c.slug}`}
                className="font-sub text-[15px] font-semibold px-2.5 py-3.5 hover:bg-white/10 transition-colors border-b-2 border-transparent hover:border-[#E11D48]"
                style={c.featuredPage ? { color: "#FDA4AF" } : {}}
              >
                {c.name}
              </Link>
            ))}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-3" data-testid="mobile-menu-trigger"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0F172A] text-white border-0 w-72 overflow-y-auto">
                <h2 className="font-headline text-2xl font-bold mb-6 mt-2">Rubriques</h2>
                <div className="flex flex-col">
                  {CATEGORIES.map((c) => (
                    <Link key={c.slug} to={c.featuredPage ? "/a-la-une" : `/rubrique/${c.slug}`}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-nav-${c.slug}`}
                      className="font-sub py-3 border-b border-white/10 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <button onClick={() => setSearchOpen((v) => !v)} className="p-3 hover:bg-white/10" data-testid="search-toggle" aria-label="Rechercher">
            {searchOpen ? <X size={20} /> : <Search size={20} />}
          </button>
        </div>

        {searchOpen && (
          <div className="border-t border-white/10 bg-[#0F172A]">
            <form onSubmit={doSearch} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2">
              <input
                autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
                placeholder="Rechercher un article, un auteur, un mot-clé…"
                className="flex-1 bg-transparent border-b border-white/30 focus:border-[#E11D48] outline-none py-2 font-sub text-white placeholder:text-slate-400"
              />
              <button type="submit" data-testid="search-submit" className="bg-[#E11D48] px-5 py-2 font-sub text-sm font-semibold rounded">Rechercher</button>
            </form>
          </div>
        )}
      </nav>

      <Ticker items={ticker} />
    </header>
  );
}
